import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ChevronDown, ChevronRight, Edit2, Trash2, Plus, GripVertical } from 'lucide-react';
import SortableQuestionSet from './SortableQuestionSet';
import QuestionSetInlineForm from './QuestionSetInlineForm';
import InlineForm from './InlineForm';

const SortableBand = ({
  band,
  expanded,
  onToggle,
  onEdit,
  onDelete,
  onToggleActive,
  onAddQuestionSet,
  expandedQuestionSets,
  setExpandedQuestionSets,
  expandedQuestions,
  setExpandedQuestions,
  handleQuestionSetReorder,
  handleQuestionReorder,
  handleOptionReorder,
  activeForm,
  setActiveForm,
  editingItem,
  setEditingItem,
  saveQuestionSet,
  saveQuestion,
  saveOption,
  deleteItem,
  toggleActive,
  sensors
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: band.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-gray-100 hover:border-[#1e3a5f] transition-all">
      <div className="p-6">
        <div className="flex items-center gap-4">
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-[#1e3a5f]">
            <GripVertical size={24} />
          </div>
          
          <button onClick={onToggle} className="text-[#1e3a5f] hover:bg-blue-50 p-2 rounded-lg transition-colors">
            {expanded ? <ChevronDown size={24} /> : <ChevronRight size={24} />}
          </button>

          <div className="flex-1">
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 bg-[#1e3a5f] text-white text-xs font-bold rounded-full">{band.code}</span>
              <h3 className="text-xl font-bold text-[#1e3a5f]">{band.title}</h3>
              {band.milestone_image && (
                <img
                  src={band.milestone_image}
                  alt="Milestone"
                  className="w-16 h-16 rounded-md object-cover"
                  onLoad={() => console.log(`Milestone image loaded for ${band.title}: ${band.milestone_image}`)}
                  onError={(e) => console.error(`Error loading milestone image for ${band.title}:`, e.target.src)}
                />
              )}
              {band.goal_image && (
                <img
                  src={band.goal_image}
                  alt="Goal"
                  className="w-16 h-16 rounded-md object-cover"
                  onLoad={() => console.log(`Goal image loaded for ${band.title}: ${band.goal_image}`)}
                  onError={(e) => console.error(`Error loading goal image for ${band.title}:`, e.target.src)}
                />
              )}
              <span className="text-sm text-gray-500">({band.age_min_months}-{band.age_max_months} months)</span>
              <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                band.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
              }`}>
                {band.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1">{band.sets?.length || 0} Question Sets</p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onToggleActive}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title={band.is_active ? 'Deactivate' : 'Activate'}
            >
              {band.is_active ? '👁️' : '👁️‍🗨️'}
            </button>
            <button
              onClick={onEdit}
              className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors"
            >
              <Edit2 size={18} />
            </button>
            <button
              onClick={onDelete}
              className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>

        {expanded && (
          <div className="mt-6 pl-12 space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-semibold text-gray-700">Question Sets</h4>
              <button
                onClick={onAddQuestionSet}
                className="px-4 py-2 bg-blue-100 text-[#1e3a5f] rounded-lg font-semibold hover:bg-blue-200 transition-colors flex items-center gap-2"
              >
                <Plus size={16} /> Add Question Set
              </button>
            </div>

            {activeForm === `questionSet-${band.id}` && (
              <InlineForm
                title={editingItem ? 'Edit Question Set' : 'Add New Question Set'}
                onCancel={() => { setActiveForm(null); setEditingItem(null); }}
              >
                <QuestionSetInlineForm
                  questionSet={editingItem}
                  bandId={band.id}
                  onSave={(data) => saveQuestionSet(band.id, data)}
                  onCancel={() => { setActiveForm(null); setEditingItem(null); }}
                />
              </InlineForm>
            )}

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(e) => handleQuestionSetReorder(band.id, e)}
            >
              <SortableContext
                items={band.sets?.map((qs) => qs.id) || []}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {band.sets?.map((questionSet) => (
                    <SortableQuestionSet
                      key={questionSet.id}
                      questionSet={questionSet}
                      bandId={band.id}
                      expanded={expandedQuestionSets[questionSet.id]}
                      onToggle={() => setExpandedQuestionSets({ ...expandedQuestionSets, [questionSet.id]: !expandedQuestionSets[questionSet.id] })}
                      expandedQuestions={expandedQuestions}
                      setExpandedQuestions={setExpandedQuestions}
                      handleQuestionReorder={handleQuestionReorder}
                      handleOptionReorder={handleOptionReorder}
                      activeForm={activeForm}
                      setActiveForm={setActiveForm}
                      editingItem={editingItem}
                      setEditingItem={setEditingItem}
                      saveQuestion={saveQuestion}
                      saveOption={saveOption}
                      deleteItem={deleteItem}
                      toggleActive={toggleActive}
                      sensors={sensors}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {(!band.sets || band.sets.length === 0) && activeForm !== `questionSet-${band.id}` && (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <p className="text-gray-500">No question sets yet. Add your first one!</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SortableBand;
